import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Smartphone, Plus, QrCode, Wifi, WifiOff, MoreVertical, 
  Trash2, Settings, Users, RefreshCw, MessageSquare, Phone, Terminal, Zap, Building2, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '@/lib/api';
import { io } from 'socket.io-client';
import WhatsAppGroups from './WhatsAppGroups';
import WhatsAppBroadcast from './WhatsAppBroadcast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_BASE = BACKEND_URL;

function BillingCard({ billingInfo, usedSessions }) {
  const { t } = useTranslation();
  const billing = billingInfo.billing || {};
  const limits = billingInfo.limits || {};
  const links = billingInfo.payment_links || {};
  const active = billingInfo.active;
  const planLabel = billing.plan === 'agencia' ? 'Small Agency' : 'Personal';
  const statusLabel = billing.status === 'trialing' ? t('dash.trialing') : active ? t('dash.active') : t('dash.suspended');
  const statusClass = billing.status === 'trialing'
    ? 'bg-amber-100 text-amber-700 border-amber-200'
    : active
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-red-100 text-red-700 border-red-200';
  const trialEnds = billing.trial_ends ? new Date(billing.trial_ends).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' }) : null;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="py-4 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold">Plan {planLabel}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusClass}`}>{statusLabel}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("wa.numbersUsage", { used: usedSessions, max: limits.max_sessions ?? '—' })}
              {billingInfo.ai_usage?.cap > 0 && (
                <> · {t("wa.aiMonthShort")}: <strong className={billingInfo.ai_usage.responses >= billingInfo.ai_usage.cap ? 'text-destructive' : ''}>{billingInfo.ai_usage.responses} / {billingInfo.ai_usage.cap}</strong></>
              )}
              {billing.status === 'trialing' && trialEnds && <> · {t("wa.trialEndsAt", { date: trialEnds })}</>}
              {!active && <> · {t("wa.regularizePayment")}</>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {links.extra_number && (
            <Button variant="outline" size="sm" asChild>
              <a href={links.extra_number} target="_blank" rel="noopener noreferrer">
                <Plus className="w-4 h-4 mr-1" /> {t("dash.buyNumber")}
              </a>
            </Button>
          )}
          {links.extra_subaccount && (
            <Button variant="outline" size="sm" asChild>
              <a href={links.extra_subaccount} target="_blank" rel="noopener noreferrer">
                <Plus className="w-4 h-4 mr-1" /> {t("dash.buySub")}
              </a>
            </Button>
          )}
          {links.ai_credits && (
            <Button variant="outline" size="sm" asChild>
              <a href={links.ai_credits} target="_blank" rel="noopener noreferrer">
                <Plus className="w-4 h-4 mr-1" /> {t("dash.buyCredits")}
              </a>
            </Button>
          )}
          {!active && links.solo && (
            <Button size="sm" asChild>
              <a href={links.solo} target="_blank" rel="noopener noreferrer">{t("wa.reactivatePlan")}</a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function WhatsApp() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [newSession, setNewSession] = useState({ name: '', priority: 1 });
  const [connecting, setConnecting] = useState(false);
  const [viewMode, setViewMode] = useState('sessions'); // 'sessions', 'groups', 'broadcast'
  const [selectedSessionForGroups, setSelectedSessionForGroups] = useState(null);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Business selector for admins
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(
    () => localStorage.getItem('leadbot_selected_business') || ''
  );
  const isAdmin = user?.role === 'admin';
  const [billingInfo, setBillingInfo] = useState(null);
  const [ghlLinked, setGhlLinked] = useState(true);
  
  // Socket.IO state
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const showQRModalRef = useRef(false);

  // Interactive menus state
  const [menus, setMenus] = useState([]);
  const [menuForm, setMenuForm] = useState(null); // null = list view, object = editing/creating
  const [savingMenu, setSavingMenu] = useState(false);
  
  // Settings modal state
  const [sessionConfig, setSessionConfig] = useState({
    openaiApiKey: '',
    anthropicApiKey: '',
    aiModel: '',
    elevenLabsApiKey: '',
    initialTag: 'wa-contact',
    invalidTag: 'invalid-wa',
    smsOnlyTag: 'sms-only',
    smsFallback: true,
    callAutoReply: true,
    callAutoReplyMessage: '',
    sendSource: false,
    spintax1: '',
    spintax2: '',
    spintax3: '',
    spintax4: '',
    spintax5: '',
    spintax6: '',
    voiceProcessor: 'none',
    voiceId: '',
    webhookMessage: '',
    webhookDisconnect: '',
    enableAgent: true,
    enableTypingIndicator: false,
    enableGroups: false,
    enableTranscription: false,
    autoJoinGroups: true,
    syncOrganicInboundToGhl: false,
    broadcastDelay: 2
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('general');
  
  // Keep ref in sync with state
  useEffect(() => {
    showQRModalRef.current = showQRModal;
  }, [showQRModal]);

  // Get business ID for current user
  const getBusinessId = useCallback(() => {
    if (isAdmin) {
      // Admin uses the selected business from dropdown
      return selectedBusinessId || null;
    }
    return user?.business_id;
  }, [user, isAdmin, selectedBusinessId]);

  // Billing status for direct (landing) customers: plan, usage and add-on links
  useEffect(() => {
    if (!isAdmin && user) {
      api.get('/billing/status').then(({ data }) => {
        if (data?.enforced) setBillingInfo(data);
      }).catch(() => null);
      api.get('/my-business/ghl').then(({ data }) => {
        setGhlLinked(Boolean(data?.connected));
      }).catch(() => null);
    }
  }, [user, isAdmin]);

  // Load businesses for admin selector
  useEffect(() => {
    if (isAdmin) {
      api.get('/businesses').then(({ data }) => {
        setBusinesses(data || []);
        // Auto-select first business if none selected
        if (data?.length > 0 && !selectedBusinessId) {
          setSelectedBusinessId(data[0].id);
        }
      }).catch(err => {
        console.error('Failed to load businesses:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Refetch sessions when selected business changes + remember the selection
  // across reloads (menus, sessions and broadcasts are scoped to this business —
  // without persistence the selector silently reset to the first business and
  // data created under another one looked "lost").
  useEffect(() => {
    if (isAdmin && selectedBusinessId) {
      localStorage.setItem('leadbot_selected_business', selectedBusinessId);
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setServiceAvailable(true); // Reset
      
      const businessId = getBusinessId();
      const url = businessId 
        ? `/whatsapp/sessions?businessId=${businessId}`
        : `/whatsapp/sessions`;
      
      const response = await api.get(url);
      const data = response.data;
      
      // Check if service is available
      if (data.error && data.error.includes('not available')) {
        setServiceAvailable(false);
        // Try to start the service automatically
        console.log('WhatsApp service not available, trying to start...');
        try {
          const startResponse = await api.post('/whatsapp-start');
          const startData = startResponse.data;
          if (startData.status === 'ok') {
            toast.success(t('wa.toastServiceStarted'));
            // Retry fetching sessions
            await new Promise(resolve => setTimeout(resolve, 2000));
            const retryResponse = await api.get(url);
            const retryData = retryResponse.data;
            setSessions(retryData.sessions || []);
            setServiceAvailable(true);
            return;
          } else {
            console.error('Failed to start WhatsApp service:', startData.message);
          }
        } catch (startError) {
          console.error('Error starting WhatsApp service:', startError);
        }
      }
      
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setServiceAvailable(false);
      toast.error(t('wa.toastLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getBusinessId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Socket.IO connection for real-time updates
  useEffect(() => {
    // Connect to the WhatsApp service via proxy
    // Note: The backend should proxy WebSocket connections too
    const socket = io(API_BASE, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket.IO connected:', socket.id);
      setSocketConnected(true);
      
      // Join business room if we have a business ID
      const businessId = getBusinessId();
      if (businessId) {
        socket.emit('join:business', businessId);
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket.IO disconnected');
      setSocketConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.log('Socket.IO connection error:', error.message);
      setSocketConnected(false);
    });

    // Handle QR code updates
    socket.on('qr', (data) => {
      console.log('QR received via Socket.IO');
      setQrCode(data.qr);
    });

    socket.on('session:qr', (data) => {
      console.log('Session QR received:', data.sessionId);
      if (selectedSession && data.sessionId === selectedSession.sessionId) {
        setQrCode(data.qr);
      }
    });

    // Handle status updates
    socket.on('status', (data) => {
      console.log('Status update:', data);
      if (data.status === 'connected') {
        toast.success(t('wa.toastConnected'));
        setShowQRModal(false);
        fetchSessions();
      }
    });

    socket.on('session:connected', (data) => {
      console.log('Session connected:', data);
      toast.success(t('wa.toastSessionConnected', { phone: data.phoneNumber || '' }));
      setShowQRModal(false);
      fetchSessions();
    });

    socket.on('session:disconnected', (data) => {
      console.log('Session disconnected:', data);
      fetchSessions();
    });

    // Handle group events
    socket.on('group:joined', (data) => {
      toast.info(t('wa.toastGroupJoined', { name: data.groupName || t('wa.newGroup') }));
    });

    return () => {
      socket.disconnect();
    };
  }, [getBusinessId, selectedSession, fetchSessions]);

  const handleCreateSession = async () => {
    if (!newSession.name.trim()) {
      toast.error(t('wa.toastNameRequired'));
      return;
    }

    try {
      // Get business_id - for regular users use their assigned business
      let businessId = user?.business_id;
      
      // For admins, use the selected business from dropdown
      if (isAdmin) {
        if (!selectedBusinessId) {
          toast.error(t('wa.toastSelectBusiness'));
          return;
        }
        businessId = selectedBusinessId;
      }

      const response = await api.post('/whatsapp/sessions', {
        business_id: businessId,
        name: newSession.name,
        priority: parseInt(newSession.priority) || 1
      });

      const data = response.data;
      
      if (data.success) {
        toast.success(t('wa.toastSessionCreated'));
        setShowAddModal(false);
        setNewSession({ name: '', priority: 1 });
        fetchSessions();
      } else {
        toast.error(data.error || t('wa.toastCreateFailed'));
      }
    } catch (error) {
      console.error('Error creating session:', error);
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        // Billing gate: plan limit reached or inactive subscription
        toast.error(detail.message || t('wa.toastPlanLimit'), { duration: 8000 });
        if (detail.payment_links) {
          const link = detail.reason === 'session_limit'
            ? detail.payment_links.extra_number
            : detail.payment_links.solo;
          toast.info(t('wa.toastExtendPlan'), {
            duration: 12000,
            action: { label: t('wa.viewPlans'), onClick: () => window.open(link, '_blank') },
          });
        }
      } else {
        toast.error(detail || t('wa.toastCreateFailed'));
      }
    }
  };

  const handleConnect = async (session) => {
    try {
      setConnecting(true);
      setSelectedSession(session);
      setShowQRModal(true);
      setQrCode(null);

      // Socket.IO is OPTIONAL. If ingress blocks /socket.io with 403, polling still works.
      try {
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit('join:session', session.sessionId);
        }
      } catch (_) { /* ignore */ }

      const response = await api.post(`/whatsapp/sessions/${session.sessionId}/connect`);
      const data = response.data;

      if (data.status === 'already_connected') {
        toast.success(t('wa.toastAlreadyConnected'));
        setShowQRModal(false);
        fetchSessions();
        return;
      }

      // --- POLLING ONLY (no socket dependency) ---
      // Poll every 1500ms for up to 60s. Stop on connect, on timeout, or if user closes modal.
      const POLL_INTERVAL_MS = 1500;
      const POLL_TIMEOUT_MS = 60000;
      const startedAt = Date.now();
      let attempt = 0;

      const pollQR = async () => {
        if (!showQRModalRef.current) return; // user closed modal

        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          console.warn(`[QR] timeout after ${POLL_TIMEOUT_MS}ms — giving up`);
          toast.error(t('wa.toastQrFailed'));
          setShowQRModal(false);
          return;
        }

        attempt += 1;
        try {
          const qrRes = await api.get(`/whatsapp/sessions/${session.sessionId}/qr`);
          const qrData = qrRes.data || {};
          console.log(
            `[QR] poll attempt #${attempt} status=${qrData.status} qr_found=${qrData.qr ? 'yes' : 'no'}`
          );

          if (qrData.status === 'connected') {
            toast.success('WhatsApp conectado correctamente');
            setShowQRModal(false);
            fetchSessions();
            return;
          }

          if (qrData.qr) {
            setQrCode(qrData.qr);
          }

          if (showQRModalRef.current) {
            setTimeout(pollQR, POLL_INTERVAL_MS);
          }
        } catch (e) {
          console.error(`[QR] poll attempt #${attempt} error:`, e?.response?.status || e?.message);
          if (showQRModalRef.current) {
            setTimeout(pollQR, POLL_INTERVAL_MS * 2);
          }
        }
      };

      setTimeout(pollQR, 1000);

    } catch (error) {
      console.error('Error connecting:', error);
      toast.error(t('wa.toastConnectFailed'));
      setShowQRModal(false);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (session) => {
    try {
      const response = await api.post(`/whatsapp/sessions/${session.sessionId}/disconnect`);
      const data = response.data;

      if (data.success || data.status === 'disconnected') {
        toast.success(t('wa.toastDisconnected'));
        setSessions((prev) => prev.map((s) =>
          s.sessionId === session.sessionId
            ? { ...s, status: 'disconnected' }
            : s
        ));
        fetchSessions();
      } else {
        toast.error(data.error || t('wa.toastDisconnectFailed'));
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error(t('wa.toastDisconnectFailed'));
    }
  };

  const handleDelete = async (session) => {
    // Open the AlertDialog (window.confirm is blocked inside the preview iframe)
    setSessionToDelete(session);
  };

  const confirmDelete = async () => {
    const session = sessionToDelete;
    if (!session) return;

    setDeleting(true);
    try {
      // Ensure disconnected first for cleaner Neonize teardown
      await api.post(`/whatsapp/sessions/${session.sessionId}/disconnect`).catch(() => null);

      const response = await api.delete(`/whatsapp/sessions/${session.sessionId}`);
      const data = response.data;

      if (data.success || data.status === 'deleted') {
        toast.success(t('wa.toastSessionDeleted'));
        setSessions((prev) => prev.filter((s) => s.sessionId !== session.sessionId));
        if (selectedSession?.sessionId === session.sessionId) {
          setSelectedSession(null);
        }
        fetchSessions();
      } else {
        toast.error(data.error || t('wa.toastDeleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error?.response?.data?.detail || t('wa.toastDeleteFailed'));
    } finally {
      setDeleting(false);
      setSessionToDelete(null);
    }
  };

  const handleExtractGroups = async (session) => {
    // Navigate to groups view for this session
    setSelectedSessionForGroups(session);
    setViewMode('groups');
  };

  // Load session configuration when settings modal opens
  const loadSessionConfig = async (session) => {
    if (!session) return;
    
    try {
      const response = await api.get(`/whatsapp/sessions/${session.sessionId}`);
      const data = response.data;
      
      if (data.session?.config) {
        const config = data.session.config;
        setSessionConfig({
          openaiApiKey: config.openaiApiKey || '',
          anthropicApiKey: config.anthropicApiKey || '',
          aiModel: config.aiModel || '',
          elevenLabsApiKey: config.elevenLabsApiKey || '',
          initialTag: config.initialTag || 'wa-contact',
          invalidTag: config.invalidTag || 'invalid-wa',
          smsOnlyTag: config.smsOnlyTag || 'sms-only',
          smsFallback: config.smsFallback !== false,
          callAutoReply: config.callAutoReply !== false,
          callAutoReplyMessage: config.callAutoReplyMessage || '',
          sendSource: config.sendSource === true,
          spintax1: config.spintax1 || '',
          spintax2: config.spintax2 || '',
          spintax3: config.spintax3 || '',
          spintax4: config.spintax4 || '',
          spintax5: config.spintax5 || '',
          spintax6: config.spintax6 || '',
          voiceProcessor: config.voiceProcessor || 'none',
          voiceId: config.voiceId || '',
          webhookMessage: config.webhooks?.onMessage || '',
          webhookDisconnect: config.webhooks?.onDisconnect || '',
          enableAgent: config.enableAgent !== false,
          enableTypingIndicator: config.enableTypingIndicator || false,
          enableGroups: config.enableGroups || false,
          enableTranscription: config.enableTranscription || false,
          autoJoinGroups: config.autoJoinGroups !== false,
          syncOrganicInboundToGhl: config.syncOrganicInboundToGhl === true,
          broadcastDelay: (config.broadcastDelay || 2000) / 1000 // Convert ms to seconds
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  // Save session configuration
  const saveSessionConfig = async () => {
    if (!selectedSession) return;
    
    try {
      setSavingSettings(true);
      
      const configToSave = {
        openaiApiKey: sessionConfig.openaiApiKey,
        anthropicApiKey: sessionConfig.anthropicApiKey,
        aiModel: sessionConfig.aiModel,
        elevenLabsApiKey: sessionConfig.elevenLabsApiKey,
        initialTag: sessionConfig.initialTag,
        invalidTag: sessionConfig.invalidTag,
        smsOnlyTag: sessionConfig.smsOnlyTag,
        smsFallback: sessionConfig.smsFallback,
        callAutoReply: sessionConfig.callAutoReply,
        callAutoReplyMessage: sessionConfig.callAutoReplyMessage,
        sendSource: sessionConfig.sendSource,
        spintax1: sessionConfig.spintax1,
        spintax2: sessionConfig.spintax2,
        spintax3: sessionConfig.spintax3,
        spintax4: sessionConfig.spintax4,
        spintax5: sessionConfig.spintax5,
        spintax6: sessionConfig.spintax6,
        voiceProcessor: sessionConfig.voiceProcessor,
        voiceId: sessionConfig.voiceId,
        enableAgent: sessionConfig.enableAgent,
        enableTypingIndicator: sessionConfig.enableTypingIndicator,
        enableGroups: sessionConfig.enableGroups,
        enableTranscription: sessionConfig.enableTranscription,
        autoJoinGroups: sessionConfig.autoJoinGroups,
        syncOrganicInboundToGhl: sessionConfig.syncOrganicInboundToGhl,
        broadcastDelay: sessionConfig.broadcastDelay * 1000, // Convert seconds to ms
        webhooks: {
          onMessage: sessionConfig.webhookMessage,
          onDisconnect: sessionConfig.webhookDisconnect
        }
      };

      const response = await api.put(`/whatsapp/sessions/${selectedSession.sessionId}`, { config: configToSave });
      const data = response.data;
      
      if (data.success) {
        toast.success(t('wa.toastConfigSaved'));
        setShowSettingsModal(false);
        fetchSessions();
      } else {
        toast.error(data.error || t('wa.toastSaveFailed'));
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(t('wa.toastConfigSaveFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  // ---- Interactive menus ----
  const emptyMenuForm = () => ({
    id: null, name: '', slug: '', title: '', body: '', footer: '',
    options: [{ id: 'opt-1', text: '', action: 'none', message: '', next_slug: '', tag: '' }],
  });

  // Menus belong to a business AND the runtime (#menu|slug, auto-replies) resolves
  // them by the SESSION's business_id. So the menus tab must always read/write the
  // business of the session being configured — never the global selector, which can
  // point elsewhere (e.g. duplicate business entries or a stale persisted selection).
  const menuBusinessId = () => selectedSession?.businessId || (isAdmin ? selectedBusinessId : null);

  const fetchMenus = async () => {
    try {
      const bizId = menuBusinessId();
      const params = bizId ? { businessId: bizId } : {};
      const response = await api.get('/whatsapp/menus', { params });
      setMenus(response.data.menus || []);
    } catch (error) {
      console.error('Error loading menus:', error);
      toast.error(error.response?.data?.detail || t('wa.toastMenusLoadFailed'));
    }
  };

  // Load menus whenever the settings modal shows the Menús tab — robust against
  // tab-trigger click timing (the trigger's onClick is not guaranteed to fire
  // before render in every Radix version, which left the list empty).
  useEffect(() => {
    if (showSettingsModal && settingsTab === 'menus' && selectedSession) {
      fetchMenus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSettingsModal, settingsTab, selectedSession]);

  const saveMenu = async () => {
    if (!menuForm) return;
    if (!menuForm.name.trim() || !menuForm.slug.trim()) {
      toast.error(t('wa.toastMenuNeedName'));
      return;
    }
    const validOptions = (menuForm.options || []).filter(o => (o.text || '').trim());
    if (!validOptions.length) {
      toast.error(t('wa.toastMenuNeedOption'));
      return;
    }
    const badChain = validOptions.find(o => o.action === 'menu' && !(o.next_slug || '').trim());
    if (badChain) {
      toast.error(t('wa.toastMenuChainMissing', { text: badChain.text }));
      return;
    }
    setSavingMenu(true);
    try {
      const payload = { ...menuForm, options: validOptions };
      const bizId = menuBusinessId();
      if (bizId) payload.businessId = bizId;
      if (menuForm.id) {
        await api.put(`/whatsapp/menus/${menuForm.id}`, payload);
      } else {
        await api.post('/whatsapp/menus', payload);
      }
      toast.success(t('wa.toastMenuSaved'));
      setMenuForm(null);
      fetchMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('wa.toastMenuSaveFailed'));
    } finally {
      setSavingMenu(false);
    }
  };

  const deleteMenu = async (menuId) => {
    try {
      await api.delete(`/whatsapp/menus/${menuId}`);
      toast.success(t('wa.toastMenuDeleted'));
      fetchMenus();
    } catch (error) {
      toast.error(t('wa.toastMenuDeleteFailed'));
    }
  };

  const updateMenuOption = (idx, patch) => {
    setMenuForm(prev => ({
      ...prev,
      options: prev.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      connected: { label: t('wa.connected'), variant: 'default', className: 'bg-green-500 hover:bg-green-500' },
      disconnected: { label: t('wa.disconnectedStatus'), variant: 'secondary', className: 'bg-gray-400' },
      connecting: { label: t('wa.connecting'), variant: 'outline', className: 'border-blue-500 text-blue-500' },
      qr_pending: { label: t('wa.qrPending'), variant: 'outline', className: 'border-yellow-500 text-yellow-500' }
    };

    const config = statusConfig[status] || statusConfig.disconnected;
    
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const handleRetryService = async () => {
    try {
      setLoading(true);
      toast.info(t('wa.toastStartingService'));
      
      const response = await api.post('/whatsapp-start');
      const data = response.data;
      
      if (data.status === 'ok') {
        toast.success(data.message || t('wa.toastServiceStartedShort'));
        await new Promise(resolve => setTimeout(resolve, 2000));
        await fetchSessions();
      } else {
        toast.error(data.message || t('wa.toastServiceStartFailed'));
      }
    } catch (error) {
      console.error('Retry error:', error);
      toast.error(t('wa.toastServerError'));
    } finally {
      setLoading(false);
    }
  };

  // If viewing groups for a session, show that view
  if (viewMode === 'groups' && selectedSessionForGroups) {
    return (
      <div className="p-6">
        <WhatsAppGroups 
          session={selectedSessionForGroups} 
          onBack={() => {
            setViewMode('sessions');
            setSelectedSessionForGroups(null);
          }}
        />
      </div>
    );
  }

  // If viewing broadcast for a session, show that view
  if (viewMode === 'broadcast' && selectedSessionForGroups) {
    return (
      <div className="p-6">
        <WhatsAppBroadcast 
          session={selectedSessionForGroups} 
          onBack={() => {
            setViewMode('sessions');
            setSelectedSessionForGroups(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="whatsapp-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("wa.title")}</h1>
          <p className="text-muted-foreground">{t("wa.subtitle")}</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Business Selector for Admins */}
          {isAdmin && (
            <div className="flex items-center gap-2 mr-4 p-2 rounded-lg bg-yellow-50 border border-yellow-200">
              <Building2 className="w-4 h-4 text-yellow-600" />
              <Select value={selectedBusinessId} onValueChange={setSelectedBusinessId}>
                <SelectTrigger className="w-[200px] bg-white border-yellow-300" data-testid="business-selector-whatsapp">
                  <SelectValue placeholder={t("wa.selectBusiness")} />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map(biz => (
                    <SelectItem key={biz.id} value={biz.id}>
                      {biz.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button 
            variant="outline"
            onClick={() => window.open('/docs.html', '_blank', 'noopener,noreferrer')}
            data-testid="docs-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            {t("wa.docs")}
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/whatsapp/commands')}
            data-testid="commands-btn"
          >
            <Terminal className="w-4 h-4 mr-2" />
            {t("wa.commands")}
          </Button>
          <Button 
            onClick={() => setShowAddModal(true)}
            data-testid="add-whatsapp-btn"
            disabled={isAdmin && !selectedBusinessId}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t("wa.addWhatsapp")}
          </Button>
        </div>
      </div>

      {/* GHL link warning: only for direct (landing) customers who must self-link their CRM.
          Whitelabel SaaS / marketplace / embed clients never see this — their bridge is managed for them. */}
      {!isAdmin && !(user?.email || "").endsWith("@leadbot.embed") && billingInfo?.enforced && !ghlLinked && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4 flex flex-wrap items-center gap-3 justify-between">
            <p className="text-sm text-amber-800">
              {t("wa.ghlBanner")}
            </p>
            <Button size="sm" onClick={() => navigate('/settings')}>{t("wa.connectNow")}</Button>
          </CardContent>
        </Card>
      )}

      {/* Plan & add-ons (only for direct/landing customers with enforced billing) */}
      {billingInfo && (
        <BillingCard billingInfo={billingInfo} usedSessions={sessions.length} />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("wa.totalConnections")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sessions.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("wa.active")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {sessions.filter(s => s.status === 'connected').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t("wa.disconnected")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">
              {sessions.filter(s => s.status !== 'connected').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Important Notice - WhatsApp Business Required */}
      <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="whatsapp-business-notice">
        <div className="p-2 rounded-full bg-amber-100">
          <Smartphone className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h4 className="font-semibold text-amber-800">{t("wa.businessNoticeTitle")}</h4>
          <p className="text-sm text-amber-700 mt-1">{t("wa.businessNotice")}</p>
        </div>
      </div>

      {/* Sessions List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("wa.connectedNumbers")}</CardTitle>
              <CardDescription>{t("wa.connectedNumbersSub")}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSessions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("wa.refresh")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t("wa.loadingSessions")}
            </div>
          ) : !serviceAvailable ? (
            <div className="text-center py-12">
              <WifiOff className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t("wa.serviceDown")}</h3>
              <p className="text-muted-foreground mb-4">{t("wa.serviceDownSub")}</p>
              <Button onClick={handleRetryService} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {t("wa.retryConnection")}
              </Button>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <Smartphone className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t("wa.noWhatsapp")}</h3>
              <p className="text-muted-foreground mb-4">{t("wa.noWhatsappSub")}</p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t("wa.addWhatsapp")}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div 
                  key={session.sessionId}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  data-testid={`session-${session.sessionId}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        session.status === 'connected' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <Smartphone className={`w-6 h-6 ${
                          session.status === 'connected' ? 'text-green-600' : 'text-gray-400'
                        }`} />
                      </div>
                      {session.status === 'connected' && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{session.name}</span>
                        <Badge variant="outline" className="text-xs">
                          #{session.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {session.phoneNumber || t("wa.noNumber")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {getStatusBadge(session.status)}
                    
                    {session.status !== 'connected' ? (
                      <Button 
                        data-testid={`session-connect-${session.sessionId}`}
                        size="sm" 
                        onClick={() => handleConnect(session)}
                        disabled={connecting}
                      >
                        <QrCode className="w-4 h-4 mr-2" />
                        {t("wa.connect")}
                      </Button>
                    ) : (
                      <Button 
                        data-testid={`session-disconnect-${session.sessionId}`}
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDisconnect(session)}
                      >
                        <WifiOff className="w-4 h-4 mr-2" />
                        {t("wa.disconnect")}
                      </Button>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button data-testid={`session-menu-trigger-${session.sessionId}`} variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`session-menu-settings-${session.sessionId}`} onClick={() => {
                          setSelectedSession(session);
                          setSettingsTab('general');
                          loadSessionConfig(session);
                          setShowSettingsModal(true);
                        }}>
                          <Settings className="w-4 h-4 mr-2" />
                          {t("wa.config")}
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`session-menu-groups-${session.sessionId}`} onClick={() => handleExtractGroups(session)}>
                          <Users className="w-4 h-4 mr-2" />
                          {t("wa.groups")}
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`session-menu-broadcast-${session.sessionId}`} onClick={() => {
                          setSelectedSessionForGroups(session);
                          setViewMode('broadcast');
                        }}>
                          <MessageSquare className="w-4 h-4 mr-2" />
                          {t("wa.menuBroadcast")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          data-testid={`session-menu-delete-${session.sessionId}`}
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleDelete(session)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t("wa.menuDelete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Session Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("wa.addWhatsapp")}</DialogTitle>
            <DialogDescription>
              {t("wa.addDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("wa.nameLabel")}</Label>
              <Input
                id="name"
                placeholder={t("wa.namePh")}
                value={newSession.name}
                onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">{t("wa.priorityLabel")}</Label>
              <Input
                id="priority"
                type="number"
                min="1"
                value={newSession.priority}
                onChange={(e) => setNewSession({ ...newSession, priority: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {t("wa.priorityHint")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              {t("wa.cancel")}
            </Button>
            <Button onClick={handleCreateSession}>
              {t("wa.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Session Confirmation (replaces window.confirm which is blocked inside the preview iframe) */}
      <AlertDialog open={!!sessionToDelete} onOpenChange={(open) => { if (!open) setSessionToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("wa.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("wa.deleteDesc", { name: sessionToDelete?.name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-session-cancel" disabled={deleting}>
              {t("wa.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-session-confirm"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? t('wa.deleting') : t('wa.menuDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* QR Code Modal */}
      <Dialog open={showQRModal} onOpenChange={(open) => {
        setShowQRModal(open);
        if (!open && socketRef.current && selectedSession) {
          socketRef.current.emit('leave:session', selectedSession.sessionId);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {t("wa.qrTitle")}
              {socketConnected && (
                <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
                  <Zap className="w-3 h-3 mr-1" />
                  {t("wa.qrLive")}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {t("wa.qrDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            {qrCode ? (
              <div className="relative">
                <img src={qrCode} alt="QR Code" className="w-64 h-64 rounded-lg" />
                {socketConnected && (
                  <div className="absolute -bottom-2 -right-2 bg-green-500 text-white rounded-full p-1">
                    <Zap className="w-4 h-4" />
                  </div>
                )}
              </div>
            ) : (
              <div className="w-64 h-64 bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{t("wa.generatingQr")}</p>
                  {!socketConnected && (
                    <p className="text-xs text-yellow-600 mt-2">{t("wa.connectingRealtime")}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowQRModal(false);
              if (socketRef.current && selectedSession) {
                socketRef.current.emit('leave:session', selectedSession.sessionId);
              }
            }}>
              {t("wa.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("wa.configTitle")}: {selectedSession?.name}</DialogTitle>
            <DialogDescription>{t("wa.configSub")}</DialogDescription>
          </DialogHeader>
          <Tabs value={settingsTab} onValueChange={setSettingsTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="general">{t("wa.tabGeneral")}</TabsTrigger>
              <TabsTrigger value="voice">{t("wa.tabVoice")}</TabsTrigger>
              <TabsTrigger value="webhooks">{t("wa.tabWebhooks")}</TabsTrigger>
              <TabsTrigger value="features">{t("wa.tabFeatures")}</TabsTrigger>
              <TabsTrigger value="menus" onClick={fetchMenus}>{t("wa.tabMenus")}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>OpenAI API Key</Label>
                <Input 
                  type="password" 
                  placeholder="sk-..." 
                  value={sessionConfig.openaiApiKey}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, openaiApiKey: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">{t("wa.openaiNote")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("wa.claudeKey")}</Label>
                <Input 
                  type="password" 
                  placeholder="sk-ant-..." 
                  value={sessionConfig.anthropicApiKey}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, anthropicApiKey: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">{t("wa.claudeNote")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("wa.aiModel")}</Label>
                <Input 
                  placeholder="gpt-4o-mini / claude-sonnet-4-5" 
                  value={sessionConfig.aiModel}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, aiModel: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>ElevenLabs API Key</Label>
                <Input 
                  type="password" 
                  placeholder="..." 
                  value={sessionConfig.elevenLabsApiKey}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, elevenLabsApiKey: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">{t("wa.elevenNote")}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("wa.initialTag")}</Label>
                  <Input 
                    placeholder="wa-contact" 
                    value={sessionConfig.initialTag}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, initialTag: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">{t("wa.initialTagNote")}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t("wa.invalidTag")}</Label>
                  <Input 
                    placeholder="invalid-wa" 
                    value={sessionConfig.invalidTag}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, invalidTag: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">{t("wa.invalidTagNote")}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("wa.smsOnlyTag")}</Label>
                <Input 
                  placeholder="sms-only" 
                  value={sessionConfig.smsOnlyTag}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, smsOnlyTag: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">{t("wa.smsOnlyTagNote")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("wa.broadcastDelay")}</Label>
                <div className="flex items-center gap-4">
                  <Input 
                    type="number"
                    min={1}
                    max={30}
                    value={sessionConfig.broadcastDelay}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, broadcastDelay: Math.max(1, Math.min(30, parseInt(e.target.value) || 2)) }))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">{t("wa.broadcastDelayNote")}</span>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="voice" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t("wa.voiceProcessor")}</Label>
                <select 
                  className="w-full border rounded-md p-2 bg-background"
                  value={sessionConfig.voiceProcessor}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, voiceProcessor: e.target.value }))}
                >
                  <option value="none">{t("wa.vpNone")}</option>
                  <option value="openai">OpenAI TTS</option>
                  <option value="elevenlabs">ElevenLabs</option>
                </select>
              </div>
              {sessionConfig.voiceProcessor === 'elevenlabs' && (
                <div className="space-y-2">
                  <Label>Voice ID (ElevenLabs)</Label>
                  <Input 
                    placeholder="Voice ID" 
                    value={sessionConfig.voiceId}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, voiceId: e.target.value }))}
                  />
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="webhooks" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t("wa.webhookMsg")}</Label>
                <Input 
                  placeholder="https://..." 
                  value={sessionConfig.webhookMessage}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, webhookMessage: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">{t("wa.webhookMsgNote")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("wa.webhookDisc")}</Label>
                <Input 
                  placeholder="https://..." 
                  value={sessionConfig.webhookDisconnect}
                  onChange={(e) => setSessionConfig(prev => ({ ...prev, webhookDisconnect: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">{t("wa.webhookDiscNote")}</p>
              </div>
            </TabsContent>
            
            <TabsContent value="features" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="enableAgent" 
                    checked={sessionConfig.enableAgent}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, enableAgent: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="enableAgent">
                    {t("wa.enableAgent")}
                    <span className="block text-xs font-normal text-muted-foreground">{t("wa.enableAgentNote")}</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="smsFallback" 
                    checked={sessionConfig.smsFallback}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, smsFallback: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="smsFallback">{t("wa.smsFallback")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="callAutoReply" 
                    checked={sessionConfig.callAutoReply}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, callAutoReply: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="callAutoReply">{t("wa.callAutoReply")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="typingIndicator" 
                    checked={sessionConfig.enableTypingIndicator}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, enableTypingIndicator: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="typingIndicator">{t("wa.typingIndicator")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="enableGroups" 
                    checked={sessionConfig.enableGroups}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, enableGroups: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="enableGroups">{t("wa.groupsSupport")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="enableTranscription" 
                    checked={sessionConfig.enableTranscription}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, enableTranscription: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="enableTranscription">{t("wa.audioTranscription")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="sendSource" 
                    checked={sessionConfig.sendSource}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, sendSource: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="sendSource">{t("wa.showSourceLine")}</Label>
                </div>
              </div>
              {sessionConfig.callAutoReply && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2">{t("wa.waCallsTitle")}</h4>
                  <div className="space-y-2">
                    <Label>{t("wa.callsReplyLabel")}</Label>
                    <Input 
                      placeholder={t("wa.callsReplyPh")} 
                      value={sessionConfig.callAutoReplyMessage}
                      onChange={(e) => setSessionConfig(prev => ({ ...prev, callAutoReplyMessage: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("wa.callsReplyNote")}
                    </p>
                  </div>
                </div>
              )}
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">{t("wa.groupsConfigTitle")}</h4>
                <div className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    id="autoJoinGroups" 
                    checked={sessionConfig.autoJoinGroups}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, autoJoinGroups: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="autoJoinGroups">{t("wa.autoJoin")}</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("wa.autoJoinNote")}
                </p>
              </div>

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">{t("wa.spintaxTitle")}</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  {t("wa.spintaxDesc")}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div className="space-y-1" key={n}>
                      <Label className="flex items-center gap-1.5">
                        ${`{SPINTAX_${n}}`}
                        {n === 1 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary">{t("wa.spintaxLocked")}</span>}
                      </Label>
                      {n === 1 ? (
                        <>
                          <textarea
                            className="w-full border rounded-md p-2 bg-muted/50 text-sm min-h-[60px] text-muted-foreground cursor-not-allowed"
                            readOnly
                            value={"Hola\nHola, ¿qué tal?\nHola, espero que estés muy bien\nHola, ¿cómo estás?\nHola, un gusto saludarte\nHola, ¿cómo te va?"}
                          />
                          <p className="text-[10px] text-muted-foreground">{t("wa.spintax1Note")}</p>
                        </>
                      ) : (
                        <textarea
                          className="w-full border rounded-md p-2 bg-background text-sm min-h-[60px]"
                          placeholder={"Hola, ¿cómo estás?\nQué tal, ¿cómo vas?\nBuen día, ¿cómo te va?"}
                          value={sessionConfig[`spintax${n}`]}
                          onChange={(e) => setSessionConfig(prev => ({ ...prev, [`spintax${n}`]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t("wa.spintaxInlineNote")}
                </p>
              </div>

              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">{t("wa.crmSyncTitle")}</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="syncOrganicInboundToGhl"
                    data-testid="toggle-sync-organic-inbound-ghl"
                    checked={sessionConfig.syncOrganicInboundToGhl}
                    onChange={(e) => setSessionConfig(prev => ({ ...prev, syncOrganicInboundToGhl: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="syncOrganicInboundToGhl">
                    {t("wa.syncOrganic")}
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("wa.syncOrganicNote")}</p>
              </div>
            </TabsContent>

            <TabsContent value="menus" className="space-y-4 mt-4">
              <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                {t("wa.menusNote")}
                {isAdmin && selectedSession?.businessId && (
                  <> (<strong>{(businesses.find(b => b.id === selectedSession.businessId)?.name) || t("wa.yourBusiness")}</strong>)</>
                )}
              </div>

              {!menuForm ? (
                <>
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">{t("wa.businessMenus")}</h4>
                    <Button size="sm" onClick={() => setMenuForm(emptyMenuForm())}>
                      <Plus className="w-4 h-4 mr-1" /> {t("wa.newMenu")}
                    </Button>
                  </div>
                  {menus.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t("wa.noMenus")}</p>
                  ) : (
                    <div className="space-y-2">
                      {menus.map((m) => (
                        <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{m.name} <code className="text-xs text-muted-foreground">#menu|{m.slug}</code></p>
                            <p className="text-xs text-muted-foreground">{(m.options || []).length} {t('wa.optionsCount')} · {(m.body || '').slice(0, 60)}</p>
                          </div>
                          <div className="flex space-x-1">
                            <Button size="sm" variant="outline" onClick={() => setMenuForm({ ...m })}>{t("wa.edit")}</Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteMenu(m.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("wa.menuName")}</Label>
                      <Input value={menuForm.name} onChange={(e) => setMenuForm(p => ({ ...p, name: e.target.value }))} placeholder={t("wa.menuNamePh")} />
                    </div>
                    <div>
                      <Label>{t("wa.menuSlug")}</Label>
                      <Input value={menuForm.slug} onChange={(e) => setMenuForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))} placeholder="principal" />
                    </div>
                  </div>
                  <div>
                    <Label>{t("wa.menuBody")}</Label>
                    <Input value={menuForm.body} onChange={(e) => setMenuForm(p => ({ ...p, body: e.target.value }))} placeholder={t("wa.menuBodyPh")} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("wa.menuTitle")}</Label>
                      <Input value={menuForm.title} onChange={(e) => setMenuForm(p => ({ ...p, title: e.target.value }))} />
                    </div>
                    <div>
                      <Label>{t("wa.menuFooter")}</Label>
                      <Input value={menuForm.footer} onChange={(e) => setMenuForm(p => ({ ...p, footer: e.target.value }))} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("wa.menuOptions")}</Label>
                    {menuForm.options.map((opt, idx) => (
                      <div key={idx} className="p-3 border rounded-lg space-y-2">
                        <div className="flex items-center space-x-2">
                          <Input className="flex-1" value={opt.text} placeholder={t("wa.optionTextPlaceholder", { n: idx + 1 })}
                            maxLength={25}
                            onChange={(e) => updateMenuOption(idx, { text: e.target.value })} />
                          <Select value={opt.action} onValueChange={(v) => updateMenuOption(idx, { action: v })}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{t("wa.actionNone")}</SelectItem>
                              <SelectItem value="message">{t("wa.actionMessage")}</SelectItem>
                              <SelectItem value="menu">{t("wa.actionMenu")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" disabled={menuForm.options.length <= 1}
                            onClick={() => setMenuForm(p => ({ ...p, options: p.options.filter((_, i) => i !== idx) }))}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {opt.action === 'message' && (
                          <Input value={opt.message} placeholder={t("wa.optionMessagePlaceholder")}
                            onChange={(e) => updateMenuOption(idx, { message: e.target.value })} />
                        )}
                        {opt.action === 'menu' && (
                          <Select value={opt.next_slug} onValueChange={(v) => updateMenuOption(idx, { next_slug: v })}>
                            <SelectTrigger><SelectValue placeholder={t("wa.nextMenuPlaceholder")} /></SelectTrigger>
                            <SelectContent>
                              {menus.filter(m => m.id !== menuForm.id).map(m => (
                                <SelectItem key={m.id} value={m.slug}>{m.name} (#menu|{m.slug})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Input value={opt.tag} placeholder={t("wa.optionTagPlaceholder")}
                          onChange={(e) => updateMenuOption(idx, { tag: e.target.value })} />
                      </div>
                    ))}
                    <Button size="sm" variant="outline"
                      onClick={() => setMenuForm(p => ({ ...p, options: [...p.options, { id: `opt-${p.options.length + 1}`, text: '', action: 'none', message: '', next_slug: '', tag: '' }] }))}>
                      <Plus className="w-4 h-4 mr-1" /> {t("wa.addOption")}
                    </Button>
                  </div>

                  <div className="flex justify-end space-x-2 pt-2">
                    <Button variant="outline" onClick={() => setMenuForm(null)}>{t("wa.back")}</Button>
                    <Button onClick={saveMenu} disabled={savingMenu}>
                      {savingMenu ? t('wa.saving') : (menuForm.id ? t('wa.saveMenu') : t('wa.createMenu'))}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
              {t("wa.close")}
            </Button>
            {settingsTab !== 'menus' && (
              <Button data-testid="save-session-config" onClick={saveSessionConfig} disabled={savingSettings}>
                {savingSettings ? t('wa.saving') : t('wa.saveChanges')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
